import Vue from 'vue'
import {mapActions, mapState} from "vuex";
// @ts-ignore
import Mark from 'mark.js'


const configToMark = {
    ignorePunctuation: ['.', ':', ';', ','],
    wildcards: 'enabled',
    accuracy: 'complementary',
    className: 'storyteller-mark',
    acrossElements: true,
    separateWordSearch: false,
    caseSensitive: true,
    diacritics: false,
}

const markJs = Vue.extend({
    data: () => ({
        soundActive: false,
        textArray: [],
        voices: ['Mia', 'Miguel'],
        currentVoice: 'Mia',
        defaultVoice: 'Mia',
        instance: null,
        textMarks: [],
        loading: false,
        currentTime: null,
        isChallenge: false,
        isBook: false,
        readerSound: false,
        previousMarked: '',
        volume: 25,
        rate: 1,
    }),
    computed: {
        ...mapState({
            trackIndex: (state: any) => state.bookReading.trackIndex,
            book: (state: any) => state.bookReading.book,
            testId: (state: any) => state.challenge.testId,
            currentPage: (state: any) => state.bookReading.currentPage,
            currentChapter: (state: any) => state.bookReading.currentChapter,
            challengePage: (state: any) => state.challenge.challengePage,
            activeNarration: (state: any) => state.bookReading.activeNarration,
            pageAudio: (state: any) => state.bookReading.pageAudio,
        }),
        isNarratorActive() {
            const vm: any = this;
            return vm.activeNarration
        },
    },
    watch: {
        currentTime(nValue) {
            const vm: any = this
            const found = vm.textMarks.filter((i: any) => i.time < parseInt(nValue, 10))
            if (found.length) {
                const currentText = vm.textArray[vm.textMarks.indexOf(found[found.length - 1])]
                vm.mark(currentText)
                if (vm.pageAudio.paused) {
                    vm.mark()
                    vm.setActiveNarration(false)
                    vm.readerSound = false
                }
            }
        },
        volume(nValue = 25) {
            const vm: any = this
            if (vm.pageAudio) vm.pageAudio.volume = nValue / 100
        },
        async currentPage() {
            const vm: any = this
            if (vm.pageAudio) {
                const {paused} = vm.pageAudio
                await vm.pausePageAudioAndMark()
                vm.setPageAudio(null)
                if (!paused) {
                    await vm.readCurrentText()
                    vm.closeReading()
                }
            }
        },
        async challengePage() {
            const vm: any = this
            if (vm.pageAudio) {
                const {paused} = vm.pageAudio
                await vm.pausePageAudioAndMark()
                vm.setPageAudio(null)
                if (!paused) {
                    await vm.readCurrentText()
                    vm.closeReading()
                }
            }
        },
        currentVoice(newVoice, oldVoice) {
            const vm: any = this
            if (newVoice !== oldVoice && vm.pageAudio) vm.setPageAudio(null)
        },
    },
    methods: {
        ...mapActions('bookReading', ['setTrackIndex', 'showReadaloud', 'setActiveNarration', 'setPageAudio']),
        clearInterval() {
            const vm: any = this;
            clearInterval(vm.interval)
            this.mark()
        },
        getAudioMarksForChallenge() {
            return new Promise((resolve) => {
                fetch(
                    `${process.env.VUE_APP_READER_CHALLENGE_DATA_URL}challenge_${this.testId}/${this.currentVoice || this.defaultVoice}/challenge_${this.testId}_${this.challengePage}.json`
                )
                    .then((response) => {
                        response
                            .json()
                            .then(({marks}) => resolve(marks))
                            .catch(() => resolve(false))
                    })
                    .catch(() => resolve(false))
            })
        },
        getAudioMarksForBook() {
            const vm: any = this
            const {resourceDir} = this.book
            return new Promise((resolve) => {
                fetch(
                    `${process.env.VUE_APP_READER_DATA_URL}${resourceDir}/${
                        this.currentVoice || this.defaultVoice
                    }/c_${vm.$utils.pad(this.currentChapter, 2)}_${this.currentPage}.json`
                )
                    .then((response) => {
                        response
                            .json()
                            .then(({marks}) => resolve(marks))
                            .catch(() => resolve(false))
                    })
                    .catch(() => resolve(false))
            })
        },
        async getAudioMarks() {
            let response = null;
            if (this.isBook) response = await this.getAudioMarksForBook()
            if (this.isChallenge) response = await this.getAudioMarksForChallenge()
            return Array.isArray(response) ? response : []
        },
        async getBookAudio() {
            const vm: any = this
            const {resourceDir} = vm.book
            try {
                if (!this.pageAudio)
                    return await new Audio(
                        `${
                            process.env.VUE_APP_READER_DATA_URL
                        }${resourceDir}/${this.currentVoice || this.defaultVoice}/c_${vm.$utils.pad(
                            this.currentChapter,
                            2
                        )}_${this.currentPage}.mp3`
                    )
                return null
            } catch (err) {
                return null
            }
        },
        async getChallengeAudio() {
            try {
                if (!this.pageAudio)
                    return await new Audio(
                        `${process.env.VUE_APP_READER_CHALLENGE_DATA_URL}challenge_${this.testId}/${
                            this.currentVoice || this.defaultVoice
                        }/challenge_${this.testId}_${this.challengePage}.mp3`
                    )
                return null
            } catch (err) {
                return null
            }
        },
        async getPageAudio() {
            const vm: any = this
            try {
                if (!this.pageAudio) {
                    if (this.isBook) vm.setPageAudio(await this.getBookAudio())
                    if (this.isChallenge) vm.setPageAudio(await this.getChallengeAudio())
                }

                vm.pageAudio.volume = vm.volume / 100
                vm.pageAudio.playbackRate = vm.rate

                vm.pageAudio.ontimeupdate = () => {
                    if (vm.pageAudio) vm.currentTime = (vm.pageAudio.currentTime * 1000).toFixed()
                }
            } catch (err) {
                vm.$modal.error(err.message)
            }
        },
        async pausePageAudio() {
            const vm: any = this
            if (this.pageAudio) await vm.pageAudio.pause()
        },
        async playPageAudio() {
            const vm: any = this
            if (this.pageAudio) await vm.pageAudio.play()
        },
        closeReading() {
            this.$emit('close-reading')
        },
        getPageContent() {
            const text = document.querySelector('.current-page')
            if (text) return text.textContent

            return ''
        },
        prepareTextArray() {
            const vm: any = this
            const text: any = document.querySelector('.current-page')
            if (text) {
                vm.instance = new Mark('.current-page')
                const textString = text.textContent.replaceAll(/(?:\r\n|\r|\n)/g, '/')
                vm.textArray = this.addStringTrack(textString.split(/[.,/]/))
            }
        },
        generateChallengeAudio() {
            const vm: any = this
            return new Promise((resolve) => {
                vm.$utils
                    .postData(
                        `${process.env.VUE_APP_BEEREADERS_AUDIO_GENERATOR_URL}challenge/${this.currentVoice || this.defaultVoice}/${this.testId}/${this.challengePage}`,
                        {
                            text: this.getPageContent(),
                        }
                    )
                    .then((response: any) => {
                        resolve(response)
                    })
                    .catch(() => {
                        resolve(false)
                    })
            })
        },
        generateBookAudio() {
            const vm: any = this
            return new Promise((resolve) => {
                const {resourceDir} = this.book
                vm.$utils
                    .postData(
                        `${
                            process.env.VUE_APP_BEEREADERS_AUDIO_GENERATOR_URL
                        }book/${this.currentVoice || this.defaultVoice}/${resourceDir}/${vm.$utils.pad(
                            this.currentChapter,
                            2
                        )}/${this.currentPage}`,
                        {
                            text: this.getPageContent(),
                        }
                    )
                    .then((response: any) => {
                        resolve(response)
                    })
                    .catch(() => {
                        resolve(false)
                    })
            })
        },
        async callAudioGenerator() {
            let response: any | boolean = false
            if (this.isBook) response = await this.generateBookAudio()
            if (this.isChallenge) response = await this.generateChallengeAudio()
            return response && response.ok
        },
        async readCurrentText(showOrHideUtil: boolean = true) {
            const vm: any = this
            if (!this.pageAudio) {
                this.loading = true
                this.prepareTextArray()
                vm.textMarks = await this.getAudioMarks()
                if (!this.textMarks.length) {
                    const res = await this.callAudioGenerator()
                    if (!res) {
                        this.loading = false
                        vm.snackbar = true
                        return
                    }
                    vm.textMarks = await this.getAudioMarks()
                    this.loading = false
                }

                await this.getPageAudio()
                this.loading = false
            }
            await this.audioAction()
            if (showOrHideUtil) this.showReadaloud()
        },
        async audioAction() {
            const vm: any = this
            if (vm.pageAudio && vm.pageAudio.paused) {
                await this.playPageAudio()
                this.setActiveNarration(true)
            } else {
                await this.pausePageAudioAndMark()
            }
        },
        async pausePageAudioAndMark() {
            await this.pausePageAudio()
            this.mark()
            this.setActiveNarration(false)
        },
        addStringTrack(textArray: Array<any>) {
            return [...textArray.filter((text) => text.trim().length)]
        },
        mark(text = ``) {
            const vm: any = this
            if (this.isBook){
                if (this.instance) vm.instance.unmark()
                if (text) {
                    const filter = (node: any, term: any, totalCounter: any) => {
                        return !(term === text && totalCounter > 1)
                    };
                    vm.instance.mark(text, {...configToMark, filter})
                }}
        },
        async removeChallengeAudio() {
            const vm: any = this
            await this.pausePageAudioAndMark()
            vm.setPageAudio(null)
        }
    },
    mounted() {
        this.isBook = this.$route.path.includes('book')
        this.isChallenge = this.$route.path.includes('challenge') || this.$route.path.includes('tester')
    },
    async beforeDestroy() {
        await this.pausePageAudioAndMark()
    },
})

export default markJs
